import React, { useState } from "react";
import axios from "axios";
import { Calculator, ArrowRight } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { useToast } from "../hooks/use-toast";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Estimation() {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    type: "",
    city: "",
    surface: "",
    name: "",
    phone: "",
    email: "",
  });

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.type || !form.name || !form.email) {
      toast({ title: "Champs manquants", description: "Merci de renseigner le type de bien, votre nom et votre email." });
      return;
    }
    try {
      setLoading(true);
      await axios.post(`${API}/estimations`, form);
      toast({
        title: "Demande envoyée — merci !",
        description: "Un conseiller Solès Immo vous recontacte sous 24h pour votre estimation.",
      });
      setForm({ type: "", city: "", surface: "", name: "", phone: "", email: "" });
    } catch (err) {
      toast({ title: "Erreur", description: "Une erreur est survenue. Merci de réessayer ou de nous appeler." });
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="estimation" className="relative py-24 md:py-32 bg-primary overflow-hidden">
      <div className="absolute inset-0 opacity-[0.06]" style={{ backgroundImage: "radial-gradient(circle at 20% 30%, #fff 1px, transparent 1px)", backgroundSize: "32px 32px" }} />
      <div className="relative max-w-7xl mx-auto px-5 md:px-8 grid lg:grid-cols-2 gap-14 items-center">
        <div className="text-primary-foreground">
          <span className="inline-flex items-center gap-2 tracking-[0.3em] text-xs font-medium uppercase text-primary-foreground/80">
            <Calculator className="h-4 w-4" /> Estimation de votre bien
          </span>
          <h2 className="font-serif text-4xl md:text-5xl font-semibold mt-5 leading-tight">
            Combien vaut réellement votre bien ?
          </h2>
          <p className="text-primary-foreground/85 mt-5 text-lg leading-relaxed max-w-lg">
            Une estimation juste est la clé d'une vente réussie. Nos experts du marché
            de Boulogne-Billancourt évaluent votre bien avec précision et sans engagement.
          </p>
          <ul className="mt-8 space-y-3 text-primary-foreground/90">
            <li className="flex items-center gap-3"><ArrowRight className="h-4 w-4 text-accent" /> Réponse sous 24 heures</li>
            <li className="flex items-center gap-3"><ArrowRight className="h-4 w-4 text-accent" /> Analyse locale du marché</li>
            <li className="flex items-center gap-3"><ArrowRight className="h-4 w-4 text-accent" /> Sans engagement</li>
          </ul>
        </div>

        {/* Form card */}
        <div className="bg-card rounded-3xl p-8 md:p-10 shadow-2xl">
          <h3 className="font-serif text-2xl font-semibold text-foreground mb-6">Demandez votre estimation</h3>
          <form onSubmit={submit} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>Type de bien *</Label>
                <Select value={form.type} onValueChange={(v) => update("type", v)}>
                  <SelectTrigger><SelectValue placeholder="Choisir" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="appartement">Appartement</SelectItem>
                    <SelectItem value="maison">Maison</SelectItem>
                    <SelectItem value="studio">Studio</SelectItem>
                    <SelectItem value="local">Local commercial</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Surface (m²)</Label>
                <Input value={form.surface} onChange={(e) => update("surface", e.target.value)} placeholder="Ex : 75" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Ville / Quartier</Label>
              <Input value={form.city} onChange={(e) => update("city", e.target.value)} placeholder="Boulogne-Billancourt" />
            </div>
            <div className="space-y-1.5">
              <Label>Nom complet *</Label>
              <Input value={form.name} onChange={(e) => update("name", e.target.value)} placeholder="Votre nom" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>Téléphone</Label>
                <Input value={form.phone} onChange={(e) => update("phone", e.target.value)} placeholder="06 .." />
              </div>
              <div className="space-y-1.5">
                <Label>Email *</Label>
                <Input type="email" value={form.email} onChange={(e) => update("email", e.target.value)} placeholder="vous@email.fr" />
              </div>
            </div>
            <Button type="submit" disabled={loading} className="w-full h-12 bg-accent hover:bg-accent/90 text-accent-foreground rounded-full text-base mt-2">
      Obtenir mon estimation
            </Button>
          </form>
        </div>
      </div>
    </section>
  );
}
